import { h } from 'preact'
import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import PropTypes from 'prop-types'

import { useLiveKit } from 'lib/videoCallHooks'
import { usePortal } from 'lib/portal'
import classNames from 'lib/classNames'
import Icon from 'components/Icon'
import AlertModal from 'components/AlertModal'
import Button from 'components/Button'

import ParticipantGrid from 'components/VideoCall/ParticipantGrid'
import ParticipantListPanel from 'components/VideoCall/ParticipantListPanel'
import InMeetingChat from 'components/VideoCall/InMeetingChat'
import CallControls from 'components/VideoCall/CallControls'
import JoinPage from 'components/VideoCall/JoinPage'

import './index.sass'

export default function ActiveCallOverlay({ callData, onLeave, myDisplayName, isAdmin: propIsAdmin }) {
  const { roomName, callType, callerName, chatName } = callData

  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isParticipantPanelOpen, setIsParticipantPanelOpen] = useState(false)
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [showLobby, setShowLobby] = useState(!!callData.showLobby)
  const [lobbyError, setLobbyError] = useState(null)
  const [callEnded, setCallEnded] = useState(false)
  const [kickConfirmTarget, setKickConfirmTarget] = useState(null)
  const lastMessageCountRef = useRef(0)
  const connectCalledRef = useRef(false)
  const renderPortal = usePortal()

  const isAdmin = propIsAdmin !== undefined ? propIsAdmin : !!callData.isInitiator

  const livekit = useLiveKit({
    onCallEnded() {
      onLeave()
    },
    onError(msg) {
      console.error('Call error:', msg)
      setLobbyError(msg)
    },
    onKickedOut() {
      onLeave()
    },
  })

  const connectRef = useRef(livekit.connect)
  connectRef.current = livekit.connect

  const endCallRef = useRef(livekit.endCall)
  endCallRef.current = livekit.endCall

  const previousPathRef = useRef(window.location.pathname)
  useEffect(() => {
    if (!roomName) return
    const callPath = `/video-call/${roomName}`
    if (!window.location.pathname.startsWith('/video-call/')) {
      previousPathRef.current = window.location.pathname
      window.history.pushState(null, '', callPath)
    }
  }, [roomName])

  useEffect(() => {
    if (roomName && !connectCalledRef.current && !showLobby) {
      connectCalledRef.current = true
      connectRef.current(roomName, callType, myDisplayName)
    }
  }, [roomName, showLobby])

  useEffect(() => {
    return () => {
      endCallRef.current()
    }
  }, [])

  const handleLobbyJoin = useCallback(({ displayName, cameraOn, micOn }) => {
    setShowLobby(false)
    setLobbyError(null)
    connectCalledRef.current = true
    connectRef.current(roomName, callType, displayName || myDisplayName, { cameraOn, micOn })
  }, [roomName, callType, myDisplayName])

  useEffect(() => {
    const count = livekit.chatMessages.length
    if (count > lastMessageCountRef.current && !isChatOpen) {
      setUnreadMessages(prev => prev + (count - lastMessageCountRef.current))
    }
    lastMessageCountRef.current = count
  }, [livekit.chatMessages.length, isChatOpen])

  useEffect(() => {
    if (isChatOpen) setUnreadMessages(0)
  }, [isChatOpen])

  const handleEndCall = useCallback(async () => {
    await livekit.endCall()
    setCallEnded(true)
    setTimeout(() => {
      onLeave()
    }, 2500)
  }, [livekit.endCall, onLeave])

  const handleToggleChat = useCallback(() => {
    setIsChatOpen(open => !open)
  }, [])

  const handleToggleParticipantPanel = useCallback(() => {
    setIsParticipantPanelOpen(open => !open)
  }, [])

  const handleKickParticipant = useCallback((identity) => {
    const participant = livekit.participants.find(p => p.identity === identity)
    const name = (participant && participant.name) || identity
    setKickConfirmTarget({ identity, name })
  }, [livekit.participants])

  const handleConfirmKick = useCallback(() => {
    if (kickConfirmTarget && livekit.kickParticipant) {
      livekit.kickParticipant(kickConfirmTarget.identity)
    }
    setKickConfirmTarget(null)
  }, [kickConfirmTarget, livekit.kickParticipant])

  const handleCancelKick = useCallback(() => {
    setKickConfirmTarget(null)
  }, [])

  const handleMuteParticipant = useCallback((identity) => {
    if (livekit.muteParticipant) livekit.muteParticipant(identity)
  }, [livekit.muteParticipant])

  const handleAddVirtualParticipant = useCallback(() => {
    if (livekit.addVirtualParticipant) livekit.addVirtualParticipant()
  }, [livekit.addVirtualParticipant])

  const overlayClass = classNames('ActiveCallOverlay', {
    chatOpen: isChatOpen,
  })

  const connected = livekit.isConnected

  if (callEnded) {
    const endedContent = <div className={overlayClass}>
      <div className="ActiveCallOverlay-ended">
        <div className="ActiveCallOverlay-endedIcon">
          <Icon type={callType === 'video' ? 'video' : 'audio'} size="hg" />
        </div>
        <h2 className="ActiveCallOverlay-endedTitle">Call Ended</h2>
        <p className="ActiveCallOverlay-endedSubtitle">You have left the call</p>
        <p className="ActiveCallOverlay-endedHint">Returning you back…</p>
      </div>
    </div>
    return renderPortal(endedContent)
  }

  if (showLobby) {
    const isRealHost = callerName && callerName !== myDisplayName
    const content = <div className={overlayClass}>
      <JoinPage
        meetingName={chatName || roomName}
        hostName={isRealHost ? callerName : null}
        callType={callType}
        participantCount={0}
        displayName={myDisplayName}
        onJoin={handleLobbyJoin}
        isJoining={livekit.isConnecting}
        error={lobbyError}
      />
    </div>
    return renderPortal(content)
  }

  const content = <div className={overlayClass}>
    <div className="ActiveCallOverlay-main">
      <div className="ActiveCallOverlay-header">
        <span className="ActiveCallOverlay-roomName">
          <Icon type={callType === 'video' ? 'video' : 'audio'} size="sm" />
          {' '}{chatName || (callType === 'video' ? 'Video Call' : 'Audio Call')}
        </span>
        <span
          className="ActiveCallOverlay-participantCount"
          onClick={handleToggleParticipantPanel}
          role="button"
          tabIndex={0}
        >
          <Icon type="user-list" size="sm" />
          {' '}{connected ? (1 + livekit.participants.length) : '...'}
        </span>

        {/* Virtual participant button (testing) */}
        {isAdmin && connected && (
          <button
            className="ActiveCallOverlay-virtualBtn"
            onClick={handleAddVirtualParticipant}
            title="Add virtual participant (testing)"
          >
            <Icon type="user-list" size="sm" />
            <span>+ Virtual</span>
          </button>
        )}
      </div>

      <div className="ActiveCallOverlay-grid">
        {!connected
          ? <div className="ActiveCallOverlay-connecting">
            <div className="ActiveCallOverlay-connectingIcon">
              <Icon type={
                livekit.error && livekit.error.includes('removed from this call')
                  ? 'cancel-circled'
                  : (callType === 'video' ? 'video' : 'audio')
              } size="hg" />
            </div>
            <h2 className="ActiveCallOverlay-connectingTitle">
              {livekit.error
                ? (livekit.error.includes('removed from this call')
                  ? 'Removed From Call'
                  : 'Connection Error')
                : 'Joining Call…'}
            </h2>
            <p className="ActiveCallOverlay-connectingSubtitle">
              {livekit.error
                ? (livekit.error.includes('removed from this call')
                  ? 'You were kicked from this call and cannot rejoin'
                  : livekit.error)
                : 'Setting up your audio and video'}
            </p>
            {livekit.error && (
              <button
                className="ActiveCallOverlay-connectingLeaveBtn"
                onClick={onLeave}
              >
                Go Back
              </button>
            )}
          </div>
          : <ParticipantGrid
            localVideoTrack={livekit.localVideoTrack}
            localAudioTrack={livekit.localAudioTrack}
            localScreenTrack={livekit.localScreenTrack}
            localIdentity={livekit.localIdentity}
            isMicMuted={livekit.isMicMuted}
            isVideoEnabled={livekit.isVideoEnabled}
            isScreenSharing={livekit.isScreenSharing}
            participants={livekit.participants}
            isAdmin={isAdmin}
            onKickParticipant={handleKickParticipant}
            onMuteParticipant={handleMuteParticipant}
          />
        }
      </div>

      <CallControls
        isMicMuted={livekit.isMicMuted}
        isVideoEnabled={livekit.isVideoEnabled}
        isScreenSharing={livekit.isScreenSharing}
        isChatOpen={isChatOpen}
        callQuality={livekit.callQuality}
        onToggleMute={livekit.toggleMute}
        onToggleVideo={livekit.toggleVideo}
        onToggleScreenShare={livekit.toggleScreenShare}
        onToggleChat={handleToggleChat}
        onEndCall={handleEndCall}
        chatMessageCount={unreadMessages}
      />
    </div>

    {isChatOpen && <InMeetingChat
      chatMessages={livekit.chatMessages}
      sendChatMessage={livekit.sendChatMessage}
      isOpen={isChatOpen}
      onClose={handleToggleChat}
    />}

    {isParticipantPanelOpen && connected && <ParticipantListPanel
      participants={livekit.participants}
      localIdentity={livekit.localIdentity}
      localDisplayName={myDisplayName}
      isMicMuted={livekit.isMicMuted}
      isVideoEnabled={livekit.isVideoEnabled}
      isScreenSharing={livekit.isScreenSharing}
      onClose={handleToggleParticipantPanel}
      isAdmin={isAdmin}
      onKickParticipant={handleKickParticipant}
      onMuteParticipant={handleMuteParticipant}
    />}

    {kickConfirmTarget && (
      <AlertModal
        className="ActiveCallOverlay-kickModal"
        onClose={handleCancelKick}
        buttons={[
          <Button
            type="danger"
            value="Remove from call"
            onClick={handleConfirmKick}
          />,
        ]}
      >
        <div className="ActiveCallOverlay-kickModal-body">
          <div className="ActiveCallOverlay-kickModal-icon">
            <Icon type="cancel-circled" size="lg" />
          </div>
          <h3 className="ActiveCallOverlay-kickModal-title">Remove Participant</h3>
          <p className="ActiveCallOverlay-kickModal-message">
            Are you sure you want to remove{' '}
            <strong>{kickConfirmTarget.name}</strong>{' '}
            from the call? They will not be able to rejoin.
          </p>
        </div>
      </AlertModal>
    )}
  </div>

  return renderPortal(content)
}

ActiveCallOverlay.propTypes = {
  callData: PropTypes.shape({
    roomName: PropTypes.string.isRequired,
    token: PropTypes.string,
    wsUrl: PropTypes.string,
    identity: PropTypes.string,
    callType: PropTypes.oneOf(['video', 'audio']).isRequired,
    callerName: PropTypes.string,
    chatName: PropTypes.string,
    showLobby: PropTypes.bool,
    isInitiator: PropTypes.bool,
  }).isRequired,
  onLeave: PropTypes.func.isRequired,
  myDisplayName: PropTypes.string,
  isAdmin: PropTypes.bool,
}
